import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

export type Model = {
  id: string;
  owned_by?: string;
  mode?: string;
  max_input_tokens?: number | null;
  max_output_tokens?: number | null;
  model_name?: string;
  litellm_params?: Record<string, unknown>;
  model_info?: Record<string, unknown>;
};

export function useModels() {
  return useQuery({
    queryKey: ['models'],
    queryFn: () => api<Model[]>('/api/models'),
  });
}

export function useModelInfo(modelId: string | undefined) {
  return useQuery({
    queryKey: ['model-info', modelId],
    queryFn: () =>
      api<Model>('/api/models/info', { query: { litellm_model_id: modelId } }),
    enabled: !!modelId,
  });
}

export function useCreateModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      model_name: string;
      litellm_params: { model: string; api_key?: string; api_base?: string };
    }) => api('/api/models', { method: 'POST', body: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['models'] }),
  });
}

export function useUpdateModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      model_name: string;
      litellm_params: { model: string; api_key?: string; api_base?: string };
    }) => api('/api/models/update', { method: 'POST', body: data }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['models'] });
      qc.invalidateQueries({ queryKey: ['model-info', vars.model_name] });
    },
  });
}

export function useDeleteModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api('/api/models/delete', { method: 'POST', body: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['models'] }),
  });
}